import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Avatar, Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { logoutUser } from '../../../../store/actions/userActions';
import { SET_PALETTE } from '../../../../store/actions';
import { PALETTE_CHOICES, PALETTE_STORAGE_KEY } from '../../../../themes/tokens';
import { getFullName, getInitials, getRoleLabel, hasRole } from './userInfo';
import { IconCheck, IconLogout, IconSettings, IconWorldUpload } from '@tabler/icons';

const ProfileMenu = ({ anchorEl, open, onClose, userData, anchorOrigin, transformOrigin, sx }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSuperUser = hasRole(userData, 'suser');
  const palette = useSelector((state) => state.customization.palette);

  const choosePalette = (id) => {
    dispatch({ type: SET_PALETTE, palette: id });
    try {
      window.localStorage.setItem(PALETTE_STORAGE_KEY, id);
    } catch (e) {
    }
  };

  const handleLogout = async () => {
    onClose();
    await axios.get('/api/logout').catch((err) => console.error(err));
    dispatch(logoutUser());
    navigate('/');
  };

  const handleOrganizationUpload = async () => {
    onClose();
    try {
      await axios.post('/api/db/checkAndInsertOrgsFromIDM');
    } catch (error) {
      console.error(error.response?.data?.error || error);
    }
  };

  const goTo = (route) => {
    onClose();
    navigate(route);
  };

  const roleLabel = getRoleLabel(userData);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{ sx: { width: 260, ...sx } }}
      MenuListProps={{ 'aria-label': 'Paskyros meniu' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, pt: 0.75, pb: 1.25 }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>{getInitials(userData)}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }} noWrap>
            {getFullName(userData) || '…'}
          </Typography>
          {roleLabel && (
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }} noWrap>
              {roleLabel}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 0.5 }} />

      <Typography
        component="div"
        sx={{ px: 1.25, pt: 0.75, pb: 0.5, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}
      >
        Spalvų schema
      </Typography>
      {PALETTE_CHOICES.map((p) => (
        <MenuItem
          key={p.id}
          role="menuitemradio"
          aria-checked={palette === p.id}
          selected={palette === p.id}
          onClick={() => choosePalette(p.id)}
        >
          <ListItemIcon>
            <Box component="span" sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: p.swatch, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15)' }} />
          </ListItemIcon>
          <ListItemText primary={p.label} />
          {palette === p.id && <IconCheck stroke={2} size={16} />}
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />

      {isSuperUser && (
        <MenuItem onClick={handleOrganizationUpload}>
          <ListItemIcon>
            <IconWorldUpload stroke={1.8} size={18} />
          </ListItemIcon>
          <ListItemText primary="Kelti organizacijas" />
        </MenuItem>
      )}
      {isSuperUser && (
        <MenuItem onClick={() => goTo('/super-user-settings')}>
          <ListItemIcon>
            <IconSettings stroke={1.8} size={18} />
          </ListItemIcon>
          <ListItemText primary="Administracija" />
        </MenuItem>
      )}
      {isSuperUser && <Divider sx={{ my: 0.5 }} />}

      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <IconLogout stroke={1.8} size={18} />
        </ListItemIcon>
        <ListItemText primary="Atsijungti" />
      </MenuItem>
    </Menu>
  );
};

ProfileMenu.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  userData: PropTypes.object,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
  sx: PropTypes.object
};

export default ProfileMenu;
